/**
 * SwipeRecognizer — detects discrete swipe/flick gestures.
 *
 * Fires onSwipe(direction) once when a swipe is recognised on pointerup.
 * Uses both distance threshold and velocity gate — whichever triggers first.
 */

const DEFAULTS = {
    axis: 'horizontal',
    threshold: 50,
    velocity: 0.5,
};

export class SwipeRecognizer {
    constructor(config, callbacks) {
        this.config = { ...DEFAULTS, ...config };
        this.callbacks = callbacks;
        this._active = false;
        this._startState = null;
    }

    start(pointerState) {
        this._active = true;
        this._startState = pointerState;
    }

    move() {
        // Swipe is discrete — no continuous feedback during move
    }

    end(pointerState, axisLock) {
        if (!this._active || !this._startState) {
            this._reset();
            return;
        }

        // If axis lock doesn't match our config axis, this isn't our gesture
        if (axisLock && axisLock !== this.config.axis) {
            this._reset();
            return;
        }

        const dx = pointerState.currentX - this._startState.startX;
        const dy = pointerState.currentY - this._startState.startY;
        const dt = pointerState.history.at(-1).t - this._startState.startTime;

        const isHorizontal = this.config.axis === 'horizontal';
        const distance = isHorizontal ? dx : dy;
        const absDistance = Math.abs(distance);
        const velocity = dt > 0 ? absDistance / dt : 0;

        const thresholdMet = absDistance >= this.config.threshold;
        const velocityMet = velocity >= this.config.velocity;

        if (thresholdMet || velocityMet) {
            let direction;
            if (isHorizontal) {
                direction = distance < 0 ? 'left' : 'right';
            } else {
                direction = distance < 0 ? 'up' : 'down';
            }
            this.callbacks.onSwipe?.(direction);
        }

        this._reset();
    }

    pause() {
        this._active = false;
    }

    resume(pointerState) {
        // After pinch ends, don't resume swipe — let it start fresh
        this._reset();
    }

    cancel() {
        this._reset();
    }

    _reset() {
        this._active = false;
        this._startState = null;
    }

    destroy() {
        this._reset();
    }
}
